/**
 * Catálogo de fábrica (ADR 0149): o palpite do app para lojas conhecidas quando NADA da
 * pessoa responde — nem o histórico da descrição, nem regra, nem lançamentos parecidos.
 *
 * Na primeira importação de uma fatura o celular não sabe nada da pessoa, e as 40 regras
 * de fábrica cobriam IOF, farmácia, Uber e pouco mais: na fatura de teste do Santander,
 * 44 de 56 linhas vinham sem categoria, inclusive "ANTHROPIC* CLAUDE SUB" e
 * "ODONTOCLINIC COLATINA". Sugerir errado é menos ruim que não sugerir: a pessoa corrige
 * com um toque, e a correção vira regra dela.
 *
 * Não é regra gravada de propósito. A regra de fábrica nasce com prioridade 10, e a
 * prioridade vence a especificidade no desempate — um `APPLE*` de fábrica passaria na
 * frente do `APPLEADS*` que a pessoa ensinou. O catálogo fica sempre por último, não
 * aparece na tela de Categorização Automática e muda com a atualização do app.
 *
 * Os padrões seguem a gramática das regras (`*` curinga, texto sem acento nem
 * pontuação). Categoria e subcategoria são os nomes do seed em português; a versão em
 * inglês sai do seed pela posição.
 */
import categoriesData from '@/assets/seed/categories.json';
import categoriesDataEn from '@/assets/seed/categories.en.json';
import { buildBranchRegex, normalizeAutoCategorizationPattern, normalizeAutoCategorizationText } from './autoCategorization';

export interface MerchantCatalogEntry {
    patterns: readonly string[];
    /** Categoria de despesa do seed, em português. */
    category: string;
    /** Subcategoria do seed, em português; ausente = só a categoria. */
    subcategory?: string;
}

/**
 * Marcas de um propósito só, ou palavras que dizem o que é o gasto ("ODONTO", "POSTO").
 * Nunca o nome de marketplace (Shopee, Mercado Livre, Amazon sozinha): ali o nome diz
 * onde se pagou, não o que se comprou (ADR 0133).
 */
export const MERCHANT_CATALOG: readonly MerchantCatalogEntry[] = [
    // Assinaturas
    {
        patterns: [
            'ANTHROPIC*', '*ANTHROPIC*', 'CLAUDE AI*', '*CLAUDE AI*', '*CLAUDE SUB*', '*CLAUDE BY*', 'GOOGLE*CLAUDE*', 'APPLE*CLAUDE*',
            'OPENAI*', '*OPENAI*', '*CHATGPT*', '*PERPLEXITY*', '*MIDJOURNEY*', 'GOOGLE*GEMINI*', '*ELEVENLABS*', '*COPILOT*',
            'CURSOR*AI*', '*CURSOR USAGE*',
        ],
        category: 'Assinaturas',
        subcategory: 'Assinaturas de IA',
    },
    {
        patterns: [
            '*NETFLIX*', '*SPOTIFY*', '*DISNEY*', '*YOUTUBE*', '*DEEZER*', 'GLOBOPLAY*', '*GLOBOPLAY*', 'GLOBO*COMBO*', 'GLOBO*PLAY*',
            'PARAMOUNT*', '*CRUNCHYR*', 'AMAZONPRIME*', 'AMAZON*PRIME*', '*PRIME VIDEO*', 'PRIMEVIDEO*', '*TELECINE*', '*PREMIERE*',
            'DAZN*', '*STAR PLUS*', '*STARPLUS*', '*APPLE TV*', '*MELIMAIS*', 'MELI', 'MELI MAIS*', '*TWITCH*', '*HBO MAX*', 'HBOMAX*',
        ],
        category: 'Assinaturas',
        subcategory: 'Streaming',
    },
    {
        patterns: [
            '*SCRIBD*', '*MICROSOFT*', '*XBOX*', '*GAME PASS*', '*GAMEPASS*', 'APPLE COM*', '*APPLE COM*', '*ITUNES*', 'GOOGLE*',
            '*GOOGLE PLAY*', '*GOOGLE ONE*', '*ADOBE*', '*CANVA*', '*DROPBOX*', '*NOTION*', '*GITHUB*', '*FIGMA*', '*LASTPASS*',
            '*1PASSWORD*', '*NORDVPN*', '*EXPRESSVPN*', '*SURFSHARK*', 'ZOOM US*', '*ZOOM COM*', '*UBER ONE*',
        ],
        category: 'Assinaturas',
        subcategory: 'Apps / Softwares',
    },
    { patterns: ['*TINDER*', '*BUMBLE*', '*LINKEDIN*'], category: 'Assinaturas', subcategory: 'Outros' },

    // Saúde
    {
        patterns: [
            '*ODONTO*', '*CLINICA*', '*HOSPITAL*', '*LABORATORIO*', '*LAB CLINICO*', '*FLEURY*', 'DASA*', 'SABIN*', '*DIAGNOSTICO*',
            '*CONSULTORIO*', '*FISIOTERAPIA*', '*PSICOLOG*', '*DERMATO*', '*OFTALMO*', '*PEDIATR*',
        ],
        category: 'Saúde',
        subcategory: 'Consultas / Exames',
    },
    {
        patterns: [
            '*FARMACIA*', 'DROGASIL*', 'DROGARAIA*', 'DROGA RAIA*', 'RAIA*', 'PAGUE MENOS*', 'PAGUEMENOS*', 'PANVEL*', 'NISSEI*',
            'SAO JOAO FARM*', '*DROGARIA*', 'DROGAO*',
        ],
        category: 'Saúde',
        subcategory: 'Medicamentos',
    },
    {
        patterns: ['UNIMED*', 'HAPVIDA*', 'AMIL ASSIST*', 'AMIL SAUDE*', '*NOTREDAME*', '*NOTRE DAME*', '*BRADESCO SAUDE*', '*SULAMERICA SAUDE*'],
        category: 'Saúde',
        subcategory: 'Plano de Saúde',
    },
    {
        patterns: ['SMART FIT*', 'SMARTFIT*', 'BLUEFIT*', 'BODYTECH*', 'SELFIT*', '*ACADEMIA*', 'GYMPASS*', 'WELLHUB*', 'TOTALPASS*', 'TOTAL PASS*'],
        category: 'Saúde',
        subcategory: 'Outros',
    },

    // Alimentação
    {
        patterns: [
            '*RESTAURANTE*', '*PIZZA*', '*LANCHONETE*', '*LANCHES*', '*CHURRASCARIA*', '*BURGUER*', '*HAMBURGUERIA*', '*SUSHI*',
            '*TEMAKERIA*', '*CHOPERIA*', 'OUTBACK*', 'COCO BAMBU*', 'MADERO*', 'HABIBS*', 'SPOLETO*', 'GIRAFFAS*', 'BOBS*', 'BOB S*',
            'CHINA IN BOX*', 'DOMINOS*', 'DOMINO S*', 'POPEYES*', 'BK BRASIL*', 'MCDONALD*', 'MC DONALDS*', 'ARCOS DOURADOS*', '*CAFETERIA*',
        ],
        category: 'Alimentação',
        subcategory: 'Restaurante',
    },
    {
        patterns: ['ZE DELIVERY*', 'ZEDELIVERY*', 'AIQFOME*', '*DELIVERY*', '*IFOOD*'],
        category: 'Alimentação',
        subcategory: 'Delivery',
    },
    {
        patterns: [
            '*SUPERMERCADO*', '*SUPERMERC*', '*MERCEARIA*', '*MINIMERCADO*', '*MINI MERCADO*', '*MERCADINHO*', '*HORTIFRUTI*',
            '*SACOLAO*', '*ACOUGUE*', '*ATACAREJO*', 'ASSAI*', 'ATACADAO*', 'PAO DE ACUCAR*', 'SAMS CLUB*', 'MAKRO*', 'GUANABARA*',
            'ZAFFARI*', 'BISTEK*', 'SONDA*', 'MUFFATO*', 'ANGELONI*', 'CONDOR*', 'SAVEGNAGO*', 'COMPER*', 'TENDA ATACADO*',
        ],
        category: 'Alimentação',
        subcategory: 'Supermercado',
    },
    {
        patterns: [
            '*PADARIA*', '*PANIFICADORA*', '*CONFEITARIA*', '*DOCERIA*', '*SORVETE*', '*SORVETERIA*', 'GELATO*', '*GELATERIA*',
            'ACAI*', '*ACAITERIA*', 'CACAU SHOW*', 'KOPENHAGEN*', 'LINDT*', '*CAFE*',
        ],
        category: 'Alimentação',
        subcategory: 'Outros',
    },

    // Transporte
    {
        patterns: [
            'POSTO*', '*AUTO POSTO*', '*COMBUSTIVE*', 'PETROBRAS*', 'BR MANIA*', 'RAIZEN*', 'ALE COMBUST*', 'SHELL*', 'IPIRANGA*',
        ],
        category: 'Transporte',
        subcategory: 'Combustível',
    },
    {
        patterns: [
            '*ESTACIONAMENTO*', 'ESTAPAR*', '*PARKING*', 'SEM PARAR*', 'SEMPARAR*', 'CONECTCAR*', 'VELOE*', '*PEDAGIO*', 'TAGGY*',
            '*ZONA AZUL*',
        ],
        category: 'Transporte',
        subcategory: 'Estacionamento / Pedágio',
    },
    {
        patterns: ['SPTRANS*', 'CPTM*', '*BILHETE UNICO*', 'RIOCARD*', 'METROSP*', 'CLICKBUS*', 'BUSER*', '*RODOVIARIA*'],
        category: 'Transporte',
        subcategory: 'Transporte Público',
    },
    { patterns: ['CABIFY*', 'INDRIVE*', 'INDRIVER*'], category: 'Transporte', subcategory: 'Uber / 99' },
    {
        patterns: [
            '*PNEU*', '*AUTO PECAS*', '*AUTOPECAS*', '*AUTO CENTER*', '*OFICINA*', '*MECANICA*', '*LAVA JATO*', '*LAVAJATO*',
            '*LAVA RAPIDO*', 'DETRAN*', '*LICENCIAMENTO*', '*IPVA*',
        ],
        category: 'Transporte',
        subcategory: 'Outros',
    },

    // Lazer
    {
        patterns: [
            'AIRBNB*', '*AIRBNB*', 'BOOKING*', '*BOOKING COM*', 'DECOLAR*', 'HURB*', 'HOTEL URBANO*', 'CVC*', 'EXPEDIA*', 'LATAM*',
            'TAM LINHAS*', 'GOL LINHAS*', 'GOL TRANSP*', '*VOEGOL*', 'AZUL LINHAS*', 'AZUL*AEREAS*', '*VOEAZUL*', '123MILHAS*',
            '123 MILHAS*', 'SMILES*', '*HOTEL*', '*HOTEIS*', '*POUSADA*', '*HOSTEL*', '*RESORT*', 'ACCOR*', 'IBIS*', 'LOCALIZA*',
            'MOVIDA*', 'UNIDAS*', '*RENT A CAR*', 'HERTZ*',
        ],
        category: 'Lazer',
        subcategory: 'Viagens',
    },
    {
        patterns: [
            'CINEMARK*', 'CINEPOLIS*', '*CINEMA*', 'KINOPLEX*', '*INGRESSO*', 'SYMPLA*', 'EVENTIM*', 'TICKETMASTER*', 'TICKET360*',
            '*BILHETERIA*',
        ],
        category: 'Lazer',
        subcategory: 'Cinema / Eventos',
    },
    {
        patterns: ['STEAM*', '*STEAMPOWERED*', '*STEAMGAMES*', '*PLAYSTATION*', 'NINTENDO*', '*EPIC GAMES*', '*EPICGAMES*', '*ROBLOX*', '*GARENA*'],
        category: 'Lazer',
        subcategory: 'Hobbies',
    },

    // Serviços
    {
        patterns: ['VIVO*', '*VIVOEASY*', 'TELEFONICA*', 'CLARO*', 'TIM S A*', 'TIM BRASIL*', 'TIM CELULAR*', '*RECARGA CELULAR*'],
        category: 'Serviços',
        subcategory: 'Celular',
    },
    { patterns: ['SKY SERVICOS*', 'SKY BRASIL*', '*SKY SERVICOS*'], category: 'Serviços', subcategory: 'Telefonia / TV' },

    // Moradia
    {
        patterns: [
            'ENEL*', 'CEMIG*', 'COPEL*', 'LIGHT S A*', 'LIGHT SESA*', 'SABESP*', 'CEDAE*', 'COMGAS*', 'NATURGY*', 'EQUATORIAL*',
            'NEOENERGIA*', 'COELBA*', 'CELPE*', 'COSERN*', 'CPFL*', 'ELEKTRO*', 'ENERGISA*', 'CAESB*', 'COPASA*', 'EMBASA*',
            'SANEPAR*', 'CORSAN*', 'COMPESA*', 'CAGECE*', 'ULTRAGAZ*', 'LIQUIGAS*', 'SUPERGASBRAS*', '*ENERGIA ELETRICA*',
        ],
        category: 'Moradia',
        subcategory: 'Energia / Água / Gás',
    },
    { patterns: ['*CONDOMINIO*'], category: 'Moradia', subcategory: 'Condomínio' },
    { patterns: ['*ALUGUEL*', 'QUINTOANDAR*', 'QUINTO ANDAR*'], category: 'Moradia', subcategory: 'Aluguel' },
    {
        patterns: [
            'TOK STOK*', 'TOKSTOK*', 'ETNA*', 'MOBLY*', 'CASAS BAHIA*', 'PONTO FRIO*', '*FAST SHOP*', 'FASTSHOP*', 'MADEIRAMADEIRA*',
            'MADEIRA MADEIRA*', 'CAMICADO*', '*COLCHOES*', 'MOVEIS*',
        ],
        category: 'Moradia',
        subcategory: 'Móveis / Eletrodomésticos',
    },
    {
        patterns: [
            'LEROY MERLIN*', 'LEROYMERLIN*', 'TELHANORTE*', '*CONSTRUCAO*', '*HOME CENTER*', '*FERRAGENS*', '*FERRAGISTA*',
            '*MADEIREIRA*', '*MATERIAIS ELETRICOS*',
        ],
        category: 'Moradia',
        subcategory: 'Outros',
    },

    // Vestuário
    {
        patterns: [
            'RENNER*', 'LOJAS RENNER*', 'RIACHUELO*', 'MIDWAY*', 'ZARA*', 'HERING*', 'MARISA*', 'LOJAS MARISA*', 'YOUCOM*',
            'PERNAMBUCANAS*', 'TORRA TORRA*', 'TORRATORRA*', 'DAFITI*', '*ZATTINI*', 'CENTAURO*', '*MODAS*', '*CONFECCOES*', '*BOUTIQUE*',
        ],
        category: 'Vestuário',
        subcategory: 'Roupas',
    },
    { patterns: ['AREZZO*', 'SCHUTZ*', '*CALCADOS*', 'NETSHOES*'], category: 'Vestuário', subcategory: 'Calçados' },
    {
        patterns: ['*OTICA*', '*OTICAS*', 'CHILLI BEANS*', 'VIVARA*', 'PANDORA*', '*JOALHERIA*', '*RELOJOARIA*'],
        category: 'Vestuário',
        subcategory: 'Acessórios',
    },

    // Cuidados pessoais
    {
        patterns: [
            // "NATURA*" pegaria "NATURAL DA TERRA", o hortifrúti.
            'NATURA', 'NATURA PAY*', 'NATURA COSMETICOS*', 'NATURA CO*', '*BOTICARIO*', 'EUDORA*', 'AVON*', 'SEPHORA*', 'KIKO*', 'QUEM DISSE BERENICE*', '*BELEZA NA WEB*',
            'BELEZANAWEB*', '*EPOCA COSMETICOS*', '*COSMETICOS*', '*PERFUMARIA*',
        ],
        category: 'Cuidados Pessoais',
        subcategory: 'Cosméticos',
    },
    {
        patterns: ['*BARBEARIA*', '*BARBER*', '*SALAO*', '*CABELEIREIR*', '*ESTETICA*', '*MANICURE*', '*ESMALTERIA*', '*DEPILACAO*'],
        category: 'Cuidados Pessoais',
        subcategory: 'Salão / Barbearia',
    },

    // Pets
    { patterns: ['PETZ*', 'COBASI*', 'PETLOVE*', '*PET SHOP*', '*PETSHOP*'], category: 'Pets', subcategory: 'Pet Shop' },
    { patterns: ['*VETERINAR*', '*CLINICA VET*', '*HOSPITAL VET*'], category: 'Pets', subcategory: 'Veterinário' },
    { patterns: ['*RACAO*', '*RACOES*'], category: 'Pets', subcategory: 'Ração' },

    // Educação
    {
        patterns: ['UDEMY*', 'ALURA*', 'COURSERA*', 'HOTMART*', 'KIWIFY*', 'EDUZZ*', '*DUOLINGO*', '*DOMESTIKA*', '*ROCKETSEAT*'],
        category: 'Educação',
        subcategory: 'Cursos Online',
    },
    { patterns: ['*ESCOLA*', '*COLEGIO*', '*FACULDADE*', '*UNIVERSIDADE*'], category: 'Educação', subcategory: 'Escola / Faculdade' },
    {
        patterns: ['*LIVRARIA*', 'SARAIVA*', '*ESTANTE VIRTUAL*', 'ESTANTEVIRTUAL*', '*KINDLE*', '*AUDIBLE*', '*SKEELO*', '*UBOOK*'],
        category: 'Educação',
        subcategory: 'Livros',
    },
    // Autoescola: "CFC" é Centro de Formação de Condutores.
    { patterns: ['*AUTO ESCOLA*', '*AUTOESCOLA*', 'CFC*'], category: 'Educação', subcategory: 'Outros' },

    // Impostos, taxas e seguros
    { patterns: ['DARF*', '*RECEITA FEDERAL*'], category: 'Impostos e Taxas', subcategory: 'Imposto de Renda' },
    { patterns: ['*IPTU*'], category: 'Impostos e Taxas', subcategory: 'IPTU' },
    { patterns: ['*ANUIDADE*', '*TARIFA*'], category: 'Impostos e Taxas', subcategory: 'Taxas Bancárias' },
    { patterns: ['*SEGURO AUTO*', '*SEGURO AUTOMOVEL*'], category: 'Seguros', subcategory: 'Seguro Auto' },
    { patterns: ['*SEGURO VIDA*', '*SEGURO DE VIDA*'], category: 'Seguros', subcategory: 'Seguro Vida' },
    {
        patterns: ['*SEGUROS*', '*SEGURADORA*', 'YELUM*', 'PORTO SEGURO*', 'TOKIO MARINE*', 'MAPFRE*', 'ALLIANZ*'],
        category: 'Seguros',
        subcategory: 'Outros',
    },

    // Doações
    { patterns: ['*VAKINHA*', '*DOACAO*', '*DOACOES*'], category: 'Presentes e Doações', subcategory: 'Doações' },
];

type SeedCategory = { name: string; subcategories: string[] };
type SeedData = { expense: SeedCategory[] };

/** Nomes pelos quais a categoria/subcategoria do catálogo pode estar no aparelho (PT e EN). */
export interface MerchantCatalogNames {
    category: string[];
    subcategory: string[];
}

const namesByPortuguese = (() => {
    const pt = (categoriesData as SeedData).expense;
    const en = (categoriesDataEn as SeedData).expense;
    const map = new Map<string, { en: string | undefined; subcategories: Map<string, string | undefined> }>();
    pt.forEach((category, index) => {
        const english = en[index];
        map.set(category.name, {
            en: english?.name,
            subcategories: new Map(category.subcategories.map((sub, subIndex) => [sub, english?.subcategories[subIndex]])),
        });
    });
    return map;
})();

export function merchantCatalogNames(entry: MerchantCatalogEntry): MerchantCatalogNames {
    const seed = namesByPortuguese.get(entry.category);
    const category = [entry.category, seed?.en].filter((name): name is string => !!name);
    const subcategory = entry.subcategory
        ? [entry.subcategory, seed?.subcategories.get(entry.subcategory)].filter((name): name is string => !!name)
        : [];
    return { category, subcategory };
}

/**
 * Cada padrão pré-compilado, com o tamanho do texto literal. O desempate NÃO é o das
 * regras: lá o prefixo ganha 400 pontos, e `GOOGLE*` passaria na frente de `*YOUTUBE*`
 * em "GOOGLE YOUTUBE". Aqui vence o trecho literal mais longo — o que mais diz da loja.
 */
const compiledCatalog = MERCHANT_CATALOG.flatMap((entry) =>
    entry.patterns.map((pattern) => {
        const branch = normalizeAutoCategorizationPattern(pattern);
        return { entry, pattern: branch, regex: buildBranchRegex(branch), literalLength: branch.replace(/[*\s]/g, '').length };
    }),
);

/** Entrada do catálogo que casa a descrição (a de trecho literal mais longo), ou null. */
export function findMerchantCatalogEntry(description: string): { entry: MerchantCatalogEntry; pattern: string } | null {
    const normalized = normalizeAutoCategorizationText(description);
    if (normalized.length < 2) return null;
    let best: (typeof compiledCatalog)[number] | null = null;
    for (const candidate of compiledCatalog) {
        if (best && candidate.literalLength <= best.literalLength) continue;
        if (candidate.regex.test(normalized)) best = candidate;
    }
    return best ? { entry: best.entry, pattern: best.pattern } : null;
}
